import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Button from "../../Components/Button/Button";
import TextField from "../../Components/TextField/TextField";
import Dropdown from "../../Components/Dropdown/Dropdown";
import { useToast } from "../../Contexts/ToastContext/ToastContext";
import { useAuth } from "../../Contexts/AuthContext/AuthContext";
import api from "../../services/api";
import readerApi from "../../services/readerApi";
import { getSocket } from "../../services/socket";
import QuickAdd from "./QuickAdd";
import "./Dashboard.css";

const EPC_MIN_LENGTH = 4;
const EPC_MAX_LENGTH = 24;
const EPC_HEX_REGEX = /^[0-9A-Fa-f]*$/;

const Dashboard = () => {
  const { showToast } = useToast();
  const navigate = useNavigate();
  const { isManagerOrAdmin, isWorkerOrAbove, isWorker, hasAccessToRoom, isAdmin } = useAuth();

  // Navigate helper — wired into every tag row so clicking the name opens
  // the Tag Details page (the one-stop view for editing, audit history, etc.).
  const openTagDetails = (tid) => {
    if (!tid) return;
    navigate(`/tags/${encodeURIComponent(tid)}`);
  };

  // Check if current user can edit a specific tag (based on room access)
  const canEditTag = (tag) => {
    if (isManagerOrAdmin()) return true;
    if (isWorker()) {
      // Workers can edit tags in their assigned rooms or unassigned tags
      if (!tag.rid || tag.rid === 0) return true;
      return hasAccessToRoom(tag.rid);
    }
    return false;
  };

  const [tags, setTags] = useState([]);
  const [totalTags, setTotalTags] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageLimit, setPageLimit] = useState(10);
  const [loading, setLoading] = useState(false);

  const [rooms, setRooms] = useState([]);
  const [locations, setLocations] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState("all");
  const [filterWarehouse, setFilterWarehouse] = useState("all");
  const [filterLocation, setFilterLocation] = useState("all");

  const [searchTerm, setSearchTerm] = useState("");
  const [filterAntenna, setFilterAntenna] = useState("all");
  const [filterState, setFilterState] = useState("all");

  const [seedingData, setSeedingData] = useState(false);

  const [refreshDelay, setRefreshDelay] = useState(() => {
    const saved = localStorage.getItem("rfid_refresh_delay");
    return saved ? parseInt(saved) : 10;
  });

  const [stats, setStats] = useState({
    totalTags: 0,
    totalRooms: 0,
    totalLocations: 0,
    totalWarehouses: 0,
    onDelivery: 0,
    averageRSSI: 0
  });

  const [showTagModal, setShowTagModal] = useState(false);
  const [editingTag, setEditingTag] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [pageInput, setPageInput] = useState("");
  const [showDeleteAllConfirm, setShowDeleteAllConfirm] = useState(false);
  const [deleteAllStep, setDeleteAllStep] = useState(1);

  // Multi-select state
  const [selectedTags, setSelectedTags] = useState([]);
  const [showBulkEditModal, setShowBulkEditModal] = useState(false);
  const [showBulkStateModal, setShowBulkStateModal] = useState(false);
  const [bulkEditState, setBulkEditState] = useState("READY");
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [bulkEditRid, setBulkEditRid] = useState(0);

  // Expanded TID state
  const [expandedTids, setExpandedTids] = useState(new Set());

  // Smart refresh - track last known state
  const lastTagState = useRef({ count: 0, latestTimestamp: null });
  // Tracks the last realtime (SSE / socket) update timestamp per TID.
  // Polling is the slow safety net — if a polled tag's tid was just
  // updated via realtime, prefer the in-memory state over the polled
  // state, because the polled state may still be stale (Mongo write
  // is queued/async on the Python side, 50-500ms Atlas latency).
  const lastTagSseAt = useRef(new Map());
  const FRESH_WINDOW_MS = 3000;
  // Mirror of `tags` used for synchronous reads inside event handlers
  // (e.g., rid-change detection in handleTagsUpdated, where we need to
  // compare against current state but can't rely on the setTags updater
  // closure for downstream control flow under concurrent React).
  const tagsRef = useRef([]);
  // Locally hidden/deleted tags (to avoid showing them again while backend catches up)
  const hiddenDeletedTidsRef = useRef(new Set());

  // Room → antenna mapping (built from SCAN antennas)
  const [ridToAntennaMap, setRidToAntennaMap] = useState({});

  const [tagForm, setTagForm] = useState({
    tid: "",
    epc: "",
    rssi: -45,
    rid: 0,
    state: "BLOCKED"
  });

  // State options for dropdown - Only admins/managers can set ON_DELIVERY
  const stateOptions = isManagerOrAdmin()
    ? [
        { label: "Ready", value: "READY" },
        { label: "Blocked", value: "BLOCKED" },
        { label: "On Delivery", value: "ON_DELIVERY" }
      ]
    : [
        { label: "Ready", value: "READY" },
        { label: "Blocked", value: "BLOCKED" }
      ];

  const fetchTags = async (search = "", silent = false) => {
    if (!silent) setLoading(true);
    try {
      const data = await api.getTags(1, 10000);
      let fetchedTags = data.tags || [];

      const uniqueTagsMap = new Map();
      fetchedTags.forEach(tag => {
        const key = `${tag.tid}_${tag.epc}`;
        const existing = uniqueTagsMap.get(key);
        if (!existing || new Date(tag.timestamp) > new Date(existing.timestamp)) {
          uniqueTagsMap.set(key, tag);
        }
      });

      fetchedTags = Array.from(uniqueTagsMap.values());

      // Note: search filtering is now applied downstream on `filteredTags`
      // so the auto-refresh (smartRefresh) and every other refetch path
      // don't wipe the user's current search. `search` param is kept for
      // signature stability but intentionally unused here.
      void search;

      const sortedTags = fetchedTags.sort((a, b) =>
        new Date(b.timestamp) - new Date(a.timestamp)
      );

      // Filter out any tags we've already treated as deleted locally
      const visibleTags = sortedTags.filter(tag => !hiddenDeletedTidsRef.current.has(tag.tid));

      // Update last known state for smart refresh
      const latestTimestamp = visibleTags.length > 0 ? visibleTags[0].timestamp : null;
      lastTagState.current = { count: visibleTags.length, latestTimestamp };

      setTags(visibleTags);
      setTotalTags(visibleTags.length);
    } catch (error) {
      if (!silent) {
        showToast("Failed to fetch tags from server", 3000, true, "danger");
      }
      console.error(error);
      setTags([]);
      setTotalTags(0);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // Smart refresh - only fetch if there are changes
  const smartRefresh = async () => {
    try {
      const data = await api.getTags(1, 10000);
      const fetchedTags = data.tags || [];

      // Check if count changed or if there's a newer timestamp
      const uniqueTagsMap = new Map();
      fetchedTags.forEach(tag => {
        const key = `${tag.tid}_${tag.epc}`;
        const existing = uniqueTagsMap.get(key);
        if (!existing || new Date(tag.timestamp) > new Date(existing.timestamp)) {
          uniqueTagsMap.set(key, tag);
        }
      });

      const uniqueTags = Array.from(uniqueTagsMap.values());
      const sortedTags = uniqueTags.sort((a, b) =>
        new Date(b.timestamp) - new Date(a.timestamp)
      );

      const visibleTags = sortedTags.filter(tag => !hiddenDeletedTidsRef.current.has(tag.tid));

      const newCount = visibleTags.length;
      const newLatestTimestamp = visibleTags.length > 0 ? visibleTags[0].timestamp : null;

      // Only update state if there are actual changes
      const hasChanges =
        newCount !== lastTagState.current.count ||
        newLatestTimestamp !== lastTagState.current.latestTimestamp;

      if (hasChanges) {
        lastTagState.current = { count: newCount, latestTimestamp: newLatestTimestamp };
        setTags(prev => {
          const prevByTid = new Map(prev.map(t => [t.tid, t]));
          const now = Date.now();
          return visibleTags.map(polled => {
            const prevTag = prevByTid.get(polled.tid);
            const sseAt = lastTagSseAt.current.get(polled.tid);
            const isFresh = prevTag && sseAt && (now - sseAt) < FRESH_WINDOW_MS;
            // Fresh: trust realtime `state`, accept everything else from poll
            // (timestamp, rssi, name, room — those races aren't observed).
            return isFresh ? { ...polled, state: prevTag.state } : polled;
          });
        });
        setTotalTags(newCount);
      }
    } catch (error) {
      console.error("Smart refresh error:", error);
    }
  };

  const fetchRooms = async () => {
    try {
      const data = await api.getRooms({ forTagAssignment: true });
      setRooms(data.rooms || []);
    } catch (error) {
      console.error(error);
      setRooms([]);
    }
  };

  const fetchLocations = async () => {
    try {
      const data = await api.getLocations(1, 100);
      setLocations(data.locations || []);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchWarehouses = async () => {
    try {
      const data = await api.getWarehouses(1, 100);
      setWarehouses(data.warehouses || []);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchAntennaMap = async () => {
    try {
      const allAntennas = await readerApi.getAntennas();
      const map = {};
      for (const ant of allAntennas) {
        if (ant.usage === "SCAN" && ant.roomId) {
          if (!map[ant.roomId]) map[ant.roomId] = [];
          map[ant.roomId].push(ant.antennaNumber);
        }
      }
      for (const rid of Object.keys(map)) {
        map[rid] = Array.from(new Set(map[rid])).sort((a, b) => a - b);
      }
      setRidToAntennaMap(map);
    } catch (err) {
      console.error("Failed to fetch antenna map:", err);
    }
  };

  const getTagAntenna = (rid) => {
    const ants = ridToAntennaMap[rid];
    if (!ants || ants.length === 0) return "—";
    return ants.join(", ");
  };

  const createTag = async () => {
    if (!tagForm.tid || !tagForm.epc) {
      showToast("Tag ID and Product EPC are required", 2000, true, "danger");
      return;
    }
    const normalizedEpc = tagForm.epc.trim().toUpperCase();
    if (!EPC_HEX_REGEX.test(normalizedEpc)) {
      showToast("Product EPC must be hexadecimal (0-9, A-F)", 2500, true, "danger");
      return;
    }
    if (normalizedEpc.length < EPC_MIN_LENGTH || normalizedEpc.length > EPC_MAX_LENGTH) {
      showToast(`Product EPC must be ${EPC_MIN_LENGTH}-${EPC_MAX_LENGTH} hex characters`, 2500, true, "danger");
      return;
    }

    if (tagForm.rid !== 0 && tagForm.rid !== undefined && tagForm.rid !== null) {
      const roomExists = rooms.find(room => room.rid === tagForm.rid);
      if (!roomExists) {
        showToast(`Partition with ID ${tagForm.rid} does not exist`, 3000, true, "danger");
        return;
      }
    }

    try {
      const tagData = {
        ...tagForm,
        epc: normalizedEpc,
        timestamp: new Date().toISOString(),
        rid: tagForm.rid || 0,
        state: tagForm.state || "BLOCKED"
      };

      const res = await api.createTag(tagData);
      const newTag = res?.tag;
      if (newTag) {
        setTags((prev) => {
          const merged = [newTag, ...prev];
          const map = new Map();
          merged.forEach((t) => {
            const key = `${t.tid || ""}_${t.epc || ""}`;
            const existing = map.get(key);
            if (!existing || new Date(t.timestamp || 0) > new Date(existing.timestamp || 0)) map.set(key, t);
          });
          const sorted = Array.from(map.values()).sort((a, b) => new Date(b.timestamp || 0) - new Date(a.timestamp || 0));
          return sorted;
        });
        setTotalTags((n) => n + 1);
        setCurrentPage(1);
        fetchRooms();
      }
      showToast("Tag created successfully!", 2000, true, "success");
      setShowTagModal(false);
      resetTagForm();
    } catch (error) {
      showToast(error.data?.message || error.message || "Failed to create tag", 3000, true, "danger");
      console.error(error);
    }
  };

  const updateTag = async () => {
    if (!tagForm.tid || !tagForm.epc) {
      showToast("Tag ID and Product EPC are required", 2000, true, "danger");
      return;
    }
    const normalizedEpc = tagForm.epc.trim().toUpperCase();
    if (!EPC_HEX_REGEX.test(normalizedEpc)) {
      showToast("Product EPC must be hexadecimal (0-9, A-F)", 2500, true, "danger");
      return;
    }
    if (normalizedEpc.length < EPC_MIN_LENGTH || normalizedEpc.length > EPC_MAX_LENGTH) {
      showToast(`Product EPC must be ${EPC_MIN_LENGTH}-${EPC_MAX_LENGTH} hex characters`, 2500, true, "danger");
      return;
    }

    try {
      const updateData = { ...tagForm, epc: normalizedEpc, rid: tagForm.rid || 0, state: tagForm.state || "BLOCKED" };
      const data = await api.updateTag(editingTag.tid, updateData);

      // Auto-assign to warehouse if room is assigned (only when not pending)
      if (!data.pending && tagForm.rid && tagForm.rid !== 0) {
        const room = rooms.find(r => r.rid === parseInt(tagForm.rid));
        if (room && room.warehouseId) {
          try {
            await api.assignTagByTid(room.warehouseId, tagForm.tid);
          } catch (warehouseError) {
            // Silently ignore if already assigned or other warehouse errors
            console.log("Warehouse assignment:", warehouseError.message);
          }
        }
      }

      showToast(data.pending ? "Transfer request created — awaiting manager approval" : "Tag updated successfully!", 2000, true, "success");
      setShowTagModal(false);
      setEditingTag(null);
      resetTagForm();
    } catch (error) {
      showToast(error.data?.message || error.message || "Failed to update tag", 3000, true, "danger");
      console.error(error);
    }
  };

  const deleteTag = async () => {
    if (!deleteTarget) return;

    try {
      hiddenDeletedTidsRef.current = new Set([
        ...Array.from(hiddenDeletedTidsRef.current),
        deleteTarget.id,
      ]);
      setTags(prev => {
        const filtered = prev.filter(tag => !hiddenDeletedTidsRef.current.has(tag.tid));
        setTotalTags(filtered.length);
        return filtered;
      });

      await api.deleteTag(deleteTarget.id);
      showToast("Delete requested", 2000, true, "success");
      setShowDeleteConfirm(false);
      setDeleteTarget(null);
    } catch (error) {
      showToast(error.message || "Failed to delete tag", 2000, true, "danger");
      console.error(error);
    }
  };

  const confirmDelete = () => {
    if (deleteTarget?.type === "tag") {
      deleteTag();
    }
  };

  const deleteAllTags = async () => {
    setLoading(true);
    try {
      const data = await api.deleteAllTags();
      showToast(`All ${data.deletedCount} tags deleted successfully!`, 3000, true, "success");
      setShowDeleteAllConfirm(false);
      setDeleteAllStep(1);
    } catch (error) {
      showToast(error.message || "Failed to delete all tags", 3000, true, "danger");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Multi-select functions
  const toggleSelectTag = (tid) => {
    setSelectedTags(prev =>
      prev.includes(tid)
        ? prev.filter(t => t !== tid)
        : [...prev, tid]
    );
  };

  const toggleSelectAll = () => {
    if (selectedTags.length === paginatedTags.length) {
      setSelectedTags([]);
    } else {
      setSelectedTags(paginatedTags.map(tag => tag.tid));
    }
  };

  const clearSelection = () => {
    setSelectedTags([]);
  };

  const toggleExpandTid = (tid) => {
    setExpandedTids(prev => {
      const newSet = new Set(prev);
      if (newSet.has(tid)) {
        newSet.delete(tid);
      } else {
        newSet.add(tid);
      }
      return newSet;
    });
  };

  const truncateTid = (tid, maxLength = 12) => {
    if (!tid || tid.length <= maxLength) return tid;
    return `${tid.substring(0, maxLength)}...`;
  };

  const bulkDeleteTags = async () => {
    if (selectedTags.length === 0) return;

    setLoading(true);
    try {
      const tids = selectedTags.join(',');
      // Optimistically hide deleted tags locally
      hiddenDeletedTidsRef.current = new Set([
        ...Array.from(hiddenDeletedTidsRef.current),
        ...selectedTags,
      ]);
      setTags(prev => {
        const filtered = prev.filter(tag => !hiddenDeletedTidsRef.current.has(tag.tid));
        setTotalTags(filtered.length);
        return filtered;
      });

      await api.deleteTag(tids);

      showToast(`Delete requested for ${selectedTags.length} tags`, 3000, true, "success");
      setShowBulkDeleteConfirm(false);
      setSelectedTags([]);
    } catch (error) {
      showToast(error.message || "Failed to delete tags", 3000, true, "danger");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const bulkUpdateRoom = async () => {
    if (selectedTags.length === 0) return;

    setLoading(true);
    try {
      const updatePayload = {
        tags: selectedTags.map(tid => ({ tid, rid: bulkEditRid }))
      };

      const data = await api.bulkUpdateTags(updatePayload);

      // Auto-assign to warehouse if room is assigned (only when none are pending)
      const hasPending = (data.pendingCount || 0) > 0;
      if (!hasPending && bulkEditRid && bulkEditRid !== 0) {
        const room = rooms.find(r => r.rid === parseInt(bulkEditRid));
        if (room && room.warehouseId) {
          try {
            await api.bulkAssignTags(room.warehouseId, selectedTags);
          } catch (warehouseError) {
            console.log("Warehouse bulk assignment:", warehouseError.message);
          }
        }
      }

      if (data.successCount > 0) {
        const msg = hasPending
          ? `${data.successCount} tag(s) — transfer request created, awaiting manager approval${data.failedCount > 0 ? `; ${data.failedCount} failed` : ''}`
          : `Updated ${data.successCount} tags successfully${data.failedCount > 0 ? `, ${data.failedCount} failed` : ''}`;
        showToast(msg, 3000, true, data.failedCount > 0 ? "warning" : "success");
      } else {
        showToast("Failed to update tags", 3000, true, "danger");
      }

      setShowBulkEditModal(false);
      setBulkEditRid(0);
      setSelectedTags([]);
    } catch (error) {
      showToast(error.message || "Failed to update tags", 3000, true, "danger");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const bulkUpdateState = async () => {
    if (selectedTags.length === 0) return;

    setLoading(true);
    try {
      const updatePayload = {
        tags: selectedTags.map(tid => ({ tid, state: bulkEditState }))
      };
      const data = await api.bulkUpdateTags(updatePayload);
      if (data.successCount > 0) {
        showToast(
          `Updated ${data.successCount} tag(s) to ${bulkEditState}${data.failedCount > 0 ? `, ${data.failedCount} failed` : ''}`,
          3000,
          true,
          data.failedCount > 0 ? "warning" : "success"
        );
        setSelectedTags([]);
      } else {
        showToast("Failed to update state", 3000, true, "danger");
      }
      setShowBulkStateModal(false);
    } catch (error) {
      showToast(error.message || "Failed to update state", 3000, true, "danger");
    } finally {
      setLoading(false);
    }
  };

  const resetTagForm = () => {
    setTagForm({
      tid: "",
      epc: "",
      antenna: 1,
      rssi: -45,
      rid: 0,
      state: "BLOCKED"
    });
  };

  const seedDummyData = async () => {
    setSeedingData(true);
    try {
      const data = await api.seedDummyData();
      showToast(data.message || "Dummy data created successfully!", 3000, true, "success");
      await fetchTags();
      await fetchRooms();
      await fetchLocations();
      await fetchWarehouses();
    } catch (error) {
      showToast(error.message || "Failed to seed dummy data", 3000, true, "danger");
      console.error(error);
    } finally {
      setSeedingData(false);
    }
  };

  const calculateStats = () => {
    const onDelivery = tags.filter(tag => tag.state === "ON_DELIVERY").length;

    const avgRSSI = tags.length > 0
      ? (tags.reduce((sum, tag) => sum + (tag.rssi || 0), 0) / tags.length).toFixed(1)
      : 0;

    setStats({
      totalTags: totalTags,
      totalRooms: rooms.length,
      totalLocations: locations.length,
      totalWarehouses: warehouses.length,
      onDelivery: onDelivery,
      averageRSSI: avgRSSI
    });
  };

  useEffect(() => {
    fetchTags();
    fetchRooms();
    fetchLocations();
    fetchWarehouses();
    fetchAntennaMap();
  }, []);

  // Socket.io real-time tag updates
  useEffect(() => {
    const socket = getSocket();

    const handleTagsAdded = (data) => {
      if (!data?.tags?.length) return;
      const now = Date.now();
      data.tags.forEach(t => { if (t?.tid) lastTagSseAt.current.set(t.tid, now); });
      // Detect rid change BEFORE setTags so the post-call fetchRooms
      // decision is based on a synchronous read of current state.
      const prevByTidSync = new Map(tagsRef.current.map(t => [t.tid, t]));
      let ridChanged = false;
      for (const t of data.tags) {
        const p = prevByTidSync.get(t.tid);
        if (!p || p.rid !== t.rid) { ridChanged = true; break; }
      }
      setTags(prev => {
        const newTags = [...data.tags, ...prev];
        const map = new Map();
        newTags.forEach(tag => {
          const key = `${tag.tid || tag.epc || ""}_${tag.epc || ""}`;
          const ts = tag.timestamp ? new Date(tag.timestamp).getTime() : 0;
          const existing = map.get(key);
          const existingTs = existing?.timestamp ? new Date(existing.timestamp).getTime() : 0;
          if (!existing || ts >= existingTs) map.set(key, tag);
        });
        const sorted = Array.from(map.values()).sort((a, b) => (new Date(b.timestamp || 0) - new Date(a.timestamp || 0)));
        setTotalTags(sorted.length);
        lastTagState.current = { count: sorted.length, latestTimestamp: sorted[0]?.timestamp || null };
        return sorted;
      });
      // Only refetch rooms when an incoming tag's room actually changed.
      // Most realtime updates are state-only (READY→ON_DELIVERY, theft alarm)
      // and don't move tags between rooms — saves an HTTP roundtrip + re-render
      // cascade per gate read.
      if (ridChanged) fetchRooms();
    };

    const handleTagsUpdated = (data) => {
      if (!data?.tags?.length) return;
      const now = Date.now();
      data.tags.forEach(t => { if (t?.tid) lastTagSseAt.current.set(t.tid, now); });
      const updatedMap = new Map(data.tags.map(t => [t.tid, t]));
      // Detect rid change against current tags BEFORE the setTags dispatch.
      const prevByTidSync = new Map(tagsRef.current.map(t => [t.tid, t]));
      let ridChanged = false;
      for (const t of data.tags) {
        const p = prevByTidSync.get(t.tid);
        if (p && p.rid !== t.rid) { ridChanged = true; break; }
      }
      setTags(prev => {
        const updated = prev.map(tag => {
          const incoming = updatedMap.get(tag.tid);
          if (!incoming) return tag;
          // Preserve existing name if incoming has none/UNNAMED (avoids flicker before backend sends names)
          const name = incoming.name && incoming.name !== "UNNAMED" ? incoming.name : tag.name;
          return { ...incoming, name: name || incoming.name || tag.name };
        });
        const sorted = updated.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
        lastTagState.current = { count: sorted.length, latestTimestamp: sorted[0]?.timestamp || null };
        return sorted;
      });
      if (ridChanged) fetchRooms();
    };

    const handleTagsDeleted = (data) => {
      if (!data?.tids?.length) return;
      const deletedSet = new Set(data.tids);
      setTags(prev => {
        const filtered = prev.filter(tag => !deletedSet.has(tag.tid));
        setTotalTags(filtered.length);
        lastTagState.current = { count: filtered.length, latestTimestamp: filtered[0]?.timestamp || null };
        return filtered;
      });
      setSelectedTags(prev => prev.filter(tid => !deletedSet.has(tid)));
      fetchRooms();
    };

    const handleTagsDeletedAll = () => {
      setTags([]);
      setTotalTags(0);
      setSelectedTags([]);
      lastTagState.current = { count: 0, latestTimestamp: null };
      fetchRooms();
    };

    socket.on("tags:added", handleTagsAdded);
    socket.on("tags:updated", handleTagsUpdated);
    socket.on("tags:deleted", handleTagsDeleted);
    socket.on("tags:deletedAll", handleTagsDeletedAll);

    // ---- Python SSE: direct tag-state stream --------------------------
    // The Node socket above is the "through Node" path.  When Node is
    // on a remote host (Render cold start, CORS, proxy quirks) the
    // socket can miss state changes that originated from the Python
    // reader (gate detections, transfer completions).  The /tags/stream
    // SSE is served by Python itself — same machine, no hops — so
    // Dashboard state always reflects what the reader reported.
    let tagStream;
    try { tagStream = new EventSource(readerApi.tagsStreamUrl()); } catch { /* ignore */ }
    if (tagStream) {
      tagStream.onmessage = (ev) => {
        if (!ev?.data) return;
        let payload;
        try { payload = JSON.parse(ev.data); } catch { return; }
        const incoming = payload?.tag;
        if (!incoming || !incoming.tid) return;
        // Reuse the exact same reducer as the socket path so the two
        // sources stay in sync; whichever arrives first wins, the
        // other becomes a no-op merge.
        handleTagsUpdated({ tags: [incoming] });
      };
      tagStream.onerror = () => { /* browser auto-reconnects */ };
    }

    return () => {
      socket.off("tags:added", handleTagsAdded);
      socket.off("tags:updated", handleTagsUpdated);
      socket.off("tags:deleted", handleTagsDeleted);
      socket.off("tags:deletedAll", handleTagsDeletedAll);
      try { tagStream?.close(); } catch { /* ignore */ }
    };
  }, []);

  useEffect(() => {
    tagsRef.current = tags;
  }, [tags]);

  useEffect(() => {
    calculateStats();
  }, [tags, rooms, locations, warehouses, totalTags]);

  // Polling fallback - less frequent since socket handles real-time
  useEffect(() => {
    const refreshInterval = setInterval(() => {
      smartRefresh();
    }, refreshDelay * 1000);
    return () => clearInterval(refreshInterval);
  }, [refreshDelay]);

  useEffect(() => {
    localStorage.setItem("rfid_refresh_delay", refreshDelay.toString());
  }, [refreshDelay]);

  // searchTerm is applied client-side in `filteredTags` — no refetch needed
  // on every keystroke and the filter survives auto-refreshes.

  useEffect(() => {
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  }, [filterAntenna, selectedRoom, filterState, filterWarehouse, filterLocation, searchTerm]);

  // O(1) lookup tables. The previous getRoom*Id helpers ran rooms.find()
  // and warehouses.find() per call, and they were called inside tags.filter()
  // — so each render was O(tags × rooms). Memoizing the Maps and reading by
  // key turns it into O(tags), which is a meaningful win during rapid gate
  // reads when filteredTags rebuilds repeatedly.
  const roomsByRid = useMemo(() => {
    const m = new Map();
    for (const r of rooms) m.set(r.rid, r);
    return m;
  }, [rooms]);

  const warehousesById = useMemo(() => {
    const m = new Map();
    for (const w of warehouses) m.set(w._id, w);
    return m;
  }, [warehouses]);

  const getRoomWarehouseId = useCallback((rid) => {
    const room = roomsByRid.get(rid);
    return room?.warehouseId?._id || room?.warehouseId || null;
  }, [roomsByRid]);

  const getRoomLocationId = useCallback((rid) => {
    const room = roomsByRid.get(rid);
    if (room?.locationId?._id) return room.locationId._id;
    if (room?.locationId) return room.locationId;
    const whId = room?.warehouseId?._id || room?.warehouseId;
    const wh = warehousesById.get(whId);
    return wh?.locationId?._id || wh?.locationId || null;
  }, [roomsByRid, warehousesById]);

  // Search applies to Tag ID, EPC, and the product name (which the backend
  // attaches per tag via attachProductNamesToTags). Case-insensitive
  // substring match — same as the placeholder advertises.
  const searchLower = searchTerm.trim().toLowerCase();
  // Memoized so the filter only re-runs when its inputs actually change.
  // Previously this re-ran on every parent render — including the cascade
  // of renders triggered by every gate-read SSE event.
  const filteredTags = useMemo(() => tags.filter(tag => {
    const tagAntenna = ridToAntennaMap[tag.rid] || null;
    const matchesAntenna = filterAntenna === "all" || tagAntenna === parseInt(filterAntenna);
    const matchesRoom = selectedRoom === "all" || tag.rid === parseInt(selectedRoom);
    const matchesState = filterState === "all" || tag.state === filterState;
    const matchesWarehouse = filterWarehouse === "all" || getRoomWarehouseId(tag.rid) === filterWarehouse;
    const matchesLocation = filterLocation === "all" || getRoomLocationId(tag.rid) === filterLocation;
    const matchesSearch = !searchLower ||
      tag.tid?.toLowerCase().includes(searchLower) ||
      tag.epc?.toLowerCase().includes(searchLower) ||
      tag.name?.toLowerCase().includes(searchLower);
    return matchesAntenna && matchesRoom && matchesState && matchesWarehouse && matchesLocation && matchesSearch;
  }), [tags, ridToAntennaMap, filterAntenna, selectedRoom, filterState, filterWarehouse, filterLocation, searchLower, getRoomWarehouseId, getRoomLocationId]);

  const effectivePageLimit = pageLimit === -1 ? filteredTags.length || 1 : pageLimit;

  const startIndex = (currentPage - 1) * (effectivePageLimit || 1);
  const endIndex = pageLimit === -1 ? undefined : startIndex + effectivePageLimit;
  const paginatedTags = pageLimit === -1 ? filteredTags : filteredTags.slice(startIndex, endIndex);
  const totalFilteredPages = pageLimit === -1 ? 1 : Math.ceil(filteredTags.length / effectivePageLimit);

  // Antennas based on tags that match location/warehouse/room/state filters (excluding antenna filter itself)
  const tagsForAntennaOptions = useMemo(() => tags.filter(tag => {
    const matchesRoom = selectedRoom === "all" || tag.rid === parseInt(selectedRoom);
    const matchesState = filterState === "all" || tag.state === filterState;
    const matchesWarehouse = filterWarehouse === "all" || getRoomWarehouseId(tag.rid) === filterWarehouse;
    const matchesLocation = filterLocation === "all" || getRoomLocationId(tag.rid) === filterLocation;
    return matchesRoom && matchesState && matchesWarehouse && matchesLocation;
  }), [tags, selectedRoom, filterState, filterWarehouse, filterLocation, getRoomWarehouseId, getRoomLocationId]);
  const uniqueAntennas = tagsForAntennaOptions.length > 0
    ? [...new Set(tagsForAntennaOptions.map(tag => ridToAntennaMap[tag.rid]).filter(a => a))].sort((a, b) => a - b)
    : [];

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalFilteredPages) {
      setCurrentPage(newPage);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const getRSSIColor = (rssi) => {
    if (rssi >= -40) return "#4CAF50";
    if (rssi >= -60) return "#daae56";
    return "#f44336";
  };

  const getStateStyle = (state) => {
    switch (state) {
      case "READY":
        return { bg: "#DCFCE7", color: "#15803D", border: "#86EFAC", label: "Ready" };
      case "ON_DELIVERY":
        return { bg: "#FEF3C7", color: "#B45309", border: "#FCD34D", label: "On Delivery" };
      case "PENDING_REQUEST":
        return { bg: "#E0F2FE", color: "#0369A1", border: "#7DD3FC", label: "Pending Request" };
      case "BLOCKED":
      default:
        return { bg: "#FEE2E2", color: "#DC2626", border: "#FCA5A5", label: "Blocked" };
    }
  };

  const antennaOptions = [
    { label: "All Antennas", value: "all" },
    ...uniqueAntennas.map(antenna => ({
      label: `Antenna ${antenna}`,
      value: String(antenna)
    }))
  ];

  const locationFilterOptions = [
    { label: "All Locations", value: "all" },
    ...locations.map(l => ({ label: l.name, value: l._id }))
  ];

  // Cascading: warehouses filtered by selected location
  const filteredWarehouses = warehouses.filter(w => {
    if (filterLocation === "all") return true;
    const whLocId = w.locationId?._id || w.locationId;
    return whLocId === filterLocation;
  });

  const warehouseFilterOptions = [
    { label: "All Warehouses", value: "all" },
    ...filteredWarehouses.map(w => ({ label: w.name, value: w._id }))
  ];

  // Cascading: rooms filtered by selected warehouse and location
  const filteredRoomsForDropdown = rooms.filter(room => {
    if (filterWarehouse !== "all") {
      const whId = room.warehouseId?._id || room.warehouseId;
      if (whId !== filterWarehouse) return false;
    }
    if (filterLocation !== "all") {
      const locId = room.locationId?._id || room.locationId;
      const whId = room.warehouseId?._id || room.warehouseId;
      const wh = warehouses.find(w => w._id === whId);
      const roomLocId = locId || wh?.locationId?._id || wh?.locationId;
      if (roomLocId !== filterLocation) return false;
    }
    return true;
  });

  const roomOptions = [
    { label: "All Partitions", value: "all" },
    ...filteredRoomsForDropdown.map(room => ({
      label: room.roomTitle,
      value: String(room.rid)
    }))
  ];

  const getRoomHierarchyLabel = (room) => {
    const warehouseName = room.warehouseId?.name;
    const locationName = room.locationId?.name
      || locations.find(l => l._id === (room.locationId?._id || room.locationId
        || room.warehouseId?.locationId?._id || room.warehouseId?.locationId))?.name;

    if (locationName && warehouseName) {
      return `${room.roomTitle} (${locationName} → ${warehouseName})`;
    }
    if (warehouseName) {
      return `${room.roomTitle} (${warehouseName})`;
    }
    return `${room.roomTitle} (ID: ${room.rid})`;
  };

  const roomOptionsForTag = [
    { label: "Unassigned", value: "0" },
    ...rooms.map(room => ({
      label: getRoomHierarchyLabel(room),
      value: String(room.rid)
    }))
  ];

  const stateFilterOptions = [
    { label: "All States", value: "all" },
    { label: "Ready", value: "READY" },
    { label: "Blocked", value: "BLOCKED" },
    { label: "On Delivery", value: "ON_DELIVERY" },
    { label: "Pending Request", value: "PENDING_REQUEST" }
  ];

  return (
    <div className="dashboard-container">
          <div className="dashboard-content">
            <div className="dashboard-header">
              <div>
                <h1 className="dashboard-title">RFID Dashboard</h1>
                <p className="dashboard-subtitle">Real-time monitoring and management</p>
              </div>
              <div className="dfcc gap-2">
                {isManagerOrAdmin() && (
                  <>
                    <Button
                      value={seedingData ? "Seeding..." : "Seed Data"}
                      iconLeft={seedingData ? "fa-spinner fa-spin" : "fa-database"}
                      onClick={seedDummyData}
                      variant="outline"
                      padding="12px 24px"
                      disabled={seedingData}
                    />
                    <Button
                      value="New Tag"
                      iconLeft="fa-plus"
                      onClick={() => {
                        setEditingTag(null);
                        resetTagForm();
                        setShowTagModal(true);
                      }}
                      variant="outline"
                      padding="12px 24px"
                    />
                    {isAdmin() && (
                      <Button
                        value="Delete All Data"
                        iconLeft="fa-trash"
                        onClick={async () => {
                          const ok = window.confirm(
                            "This will permanently delete ALL tags, rooms, warehouses, locations, products, and logs. Are you sure?"
                          );
                          if (!ok) return;
                          setLoading(true);
                          try {
                            const data = await api.wipeAllData();
                            showToast(data.message || "All data deleted", 4000, true, "success");
                            // Clear local state
                            setTags([]);
                            setRooms([]);
                            setLocations([]);
                            setWarehouses([]);
                            setTotalTags(0);
                          } catch (error) {
                            showToast(error.message || "Failed to delete all data", 4000, true, "danger");
                          } finally {
                            setLoading(false);
                          }
                        }}
                        variant="outline"
                        padding="12px 24px"
                      />
                    )}
                  </>
                )}
              </div>
            </div>

        <QuickAdd
          locations={locations}
          warehouses={warehouses}
          onCreated={() => {
            // Refetch everything the Dashboard renders so new entities show up
            // in stats, dropdowns, and the tag table immediately.
            fetchLocations();
            fetchWarehouses();
            fetchRooms();
            fetchTags(searchTerm, true);
          }}
          onOpenTagModal={() => {
            setEditingTag(null);
            setTagForm({ tid: "", epc: "", rssi: -45, rid: 0, state: "BLOCKED" });
            setShowTagModal(true);
          }}
        />

        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon" style={{ backgroundColor: "var(--info-subtle)" }}>
              <i className="fa fa-tag" style={{ color: "var(--info)" }}></i>
            </div>
            <div className="stat-content">
              <h3 className="stat-value">{stats.totalTags}</h3>
              <p className="stat-label">Total Tags</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon" style={{ backgroundColor: "var(--warning-subtle)" }}>
              <i className="fa fa-building" style={{ color: "var(--warning)" }}></i>
            </div>
            <div className="stat-content">
              <h3 className="stat-value">{stats.totalRooms}</h3>
              <p className="stat-label">Total Partitions</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon" style={{ backgroundColor: "var(--success-subtle)" }}>
              <i className="fa fa-map-marker" style={{ color: "var(--success)" }}></i>
            </div>
            <div className="stat-content">
              <h3 className="stat-value">{stats.totalLocations}</h3>
              <p className="stat-label">Locations</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon" style={{ backgroundColor: "var(--accent-subtle)" }}>
              <i className="fa fa-cubes" style={{ color: "var(--accent)" }}></i>
            </div>
            <div className="stat-content">
              <h3 className="stat-value">{stats.totalWarehouses}</h3>
              <p className="stat-label">Warehouses</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon" style={{ backgroundColor: "#fef3c7" }}>
              <i className="fa fa-truck" style={{ color: "#d97706" }}></i>
            </div>
            <div className="stat-content">
              <h3 className="stat-value">{stats.onDelivery}</h3>
              <p className="stat-label">Out for Delivery</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon" style={{ backgroundColor: "var(--info-subtle)" }}>
              <i className="fa fa-signal" style={{ color: "var(--info)" }}></i>
            </div>
            <div className="stat-content">
              <h3 className="stat-value">{stats.averageRSSI} dBm</h3>
              <p className="stat-label">Avg Signal</p>
            </div>
          </div>
        </div>

        <div className="filters-section">
          <h2 className="section-title">Search & Filter Tags</h2>
          <div className="filters-grid">
            <TextField
              placeholder="Search by Tag ID, EPC or Product Type..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              iconLeft="fa-search"
              variant="outlined"
            />
            <Dropdown
              options={locationFilterOptions}
              value={filterLocation}
              onChange={(val) => {
                setFilterLocation(val);
                setFilterWarehouse("all");
                setSelectedRoom("all");
              }}
              placeholder="Select Location"
              variant="outlined"
            />
            <Dropdown
              options={warehouseFilterOptions}
              value={filterWarehouse}
              onChange={(val) => {
                setFilterWarehouse(val);
                setSelectedRoom("all");
              }}
              placeholder="Select Warehouse"
              variant="outlined"
            />
            <Dropdown
              options={roomOptions}
              value={selectedRoom}
              onChange={setSelectedRoom}
              placeholder="Select Partition"
              variant="outlined"
            />
            <Dropdown
              options={antennaOptions}
              value={filterAntenna}
              onChange={setFilterAntenna}
              placeholder="Select Antenna"
              variant="outlined"
              disabled={antennaOptions.length <= 1}
            />
            <Dropdown
              options={stateFilterOptions}
              value={filterState}
              onChange={setFilterState}
              placeholder="Select State"
              variant="outlined"
            />
            <Button
              value="Clear"
              iconLeft="fa-times"
              onClick={() => {
                setSearchTerm("");
                setFilterAntenna("all");
                setSelectedRoom("all");
                setFilterWarehouse("all");
                setFilterLocation("all");
                setFilterState("all");
                showToast("Filters cleared", 2000, true, "success");
              }}
              variant="outline"
              padding="12px 24px"
            />
          </div>
        </div>

        <div className="table-section">
          <div className="section-header">
            <h2 className="section-title">RFID Tags</h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              {selectedTags.length > 0 && isWorkerOrAbove() && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 12px',
                  backgroundColor: '#EFF6FF',
                  borderRadius: '8px',
                  border: '1px solid #BFDBFE'
                }}>
                  <span style={{ fontSize: '13px', fontWeight: '600', color: '#1E40AF' }}>
                    {selectedTags.length} selected
                  </span>
                  <Button
                    value="Change Partition"
                    iconLeft="fa-exchange"
                    onClick={() => {
                      setBulkEditRid(0);
                      setShowBulkEditModal(true);
                    }}
                    variant="outline"
                    padding="6px 12px"
                    style={{ fontSize: '12px' }}
                  />
                  <Button
                    value="Change State"
                    iconLeft="fa-flag"
                    onClick={() => {
                      setBulkEditState("READY");
                      setShowBulkStateModal(true);
                    }}
                    variant="outline"
                    padding="6px 12px"
                    style={{ fontSize: '12px' }}
                  />
                  {isManagerOrAdmin() && (
                    <Button
                      value="Delete"
                      iconLeft="fa-trash"
                      onClick={() => setShowBulkDeleteConfirm(true)}
                      variant="outline"
                      padding="6px 12px"
                      style={{ fontSize: '12px', borderColor: '#FCA5A5', color: '#DC2626' }}
                    />
                  )}
                  <button
                    onClick={clearSelection}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '4px',
                      color: '#64748B'
                    }}
                    title="Clear selection"
                  >
                    <i className="fa fa-times"></i>
                  </button>
                </div>
              )}
              <span className="results-count">
                Showing {paginatedTags.length} of {filteredTags.length} tags
              </span>
              <select
                value={pageLimit}
                onChange={(e) => {
                  const val = parseInt(e.target.value);
                  setPageLimit(val);
                  setCurrentPage(1);
                }}
                style={{
                  height: '36px',
                  padding: '0 12px',
                  borderRadius: '8px',
                  border: '1.5px solid #E2E8F0',
                  backgroundColor: 'white',
                  color: '#475569',
                  fontSize: '13px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                <option value={10}>10 / page</option>
                <option value={25}>25 / page</option>
                <option value={50}>50 / page</option>
                <option value={100}>100 / page</option>
                <option value={-1}>All</option>
              </select>
            </div>
          </div>

          <div className="table-container">
            {loading ? (
              <div className="loading-state">
                <i className="fa fa-spinner fa-spin" style={{ fontSize: "48px", color: "#daae56" }}></i>
                <p>Loading tags...</p>
              </div>
            ) : paginatedTags.length === 0 ? (
              <div className="empty-state">
                <i className="fa fa-inbox" style={{ fontSize: "48px", color: "#ccc" }}></i>
                <p>No tags found</p>
              </div>
            ) : (
              <table className="tags-table">
                <thead>
                  <tr>
                    {isWorkerOrAbove() && (
                      <th style={{ width: '40px', textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={paginatedTags.length > 0 && selectedTags.length === paginatedTags.length}
                          onChange={toggleSelectAll}
                          style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                          title="Select all"
                        />
                      </th>
                    )}
                    <th>Tag ID</th>
                    <th>Name</th>
                    <th>EPC</th>
                    <th>State</th>
                    <th>Antenna</th>
                    <th>Partition</th>
                    <th>RSSI</th>
                    <th>Timestamp</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedTags.map((tag, index) => (
                    <tr key={tag._id || index} style={{
                      backgroundColor: selectedTags.includes(tag.tid) ? '#EFF6FF' : undefined
                    }}>
                      {isWorkerOrAbove() && (
                        <td data-label="Select" style={{ textAlign: 'center' }}>
                          {canEditTag(tag) ? (
                            <input
                              type="checkbox"
                              checked={selectedTags.includes(tag.tid)}
                              onChange={() => toggleSelectTag(tag.tid)}
                              style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                            />
                          ) : (
                            <span style={{ color: '#cbd5e1' }}>-</span>
                          )}
                        </td>
                      )}
                      <td data-label="Tag ID">
                        <span
                          className={`tag-badge ${expandedTids.has(tag.tid) ? 'expanded' : ''}`}
                          onClick={() => toggleExpandTid(tag.tid)}
                          title={expandedTids.has(tag.tid) ? "Click to collapse" : "Click to expand"}
                        >
                          {expandedTids.has(tag.tid) ? tag.tid : truncateTid(tag.tid)}
                        </span>
                      </td>
                      <td data-label="Name">
                        <span
                          className="epc-cell fw-bold fs-6 tag-name-link"
                          onClick={() => openTagDetails(tag.tid)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => { if (e.key === "Enter") openTagDetails(tag.tid); }}
                          title="Open tag details"
                          style={{ cursor: "pointer", color: "var(--text-primary-active)", textDecoration: "underline dotted", textUnderlineOffset: 3 }}
                        >
                          {tag.name || "UNNAMED"}
                        </span>
                      </td>
                      <td data-label="EPC" className="epc-cell">{tag.epc}</td>
                      <td data-label="State">
                        <span
                          className="state-badge"
                          style={{
                            backgroundColor: getStateStyle(tag.state).bg,
                            color: getStateStyle(tag.state).color,
                            border: `1px solid ${getStateStyle(tag.state).border}`
                          }}
                        >
                          {getStateStyle(tag.state).label}
                        </span>
                      </td>
                      <td data-label="Antenna">
                        <span className="antenna-badge">
                          <i className="fa fa-wifi"></i> Ant {getTagAntenna(tag.rid)}
                        </span>
                      </td>
                      <td data-label="Partition">
                        <span className="room-badge">
                          {tag.rid === 0 ? "Unassigned" : rooms.find(r => r.rid === tag.rid)?.roomTitle || `Partition ${tag.rid}`}
                        </span>
                      </td>
                      <td data-label="RSSI">
                        <span
                          className="rssi-badge"
                          style={{
                            backgroundColor: getRSSIColor(tag.rssi) + "20",
                            color: getRSSIColor(tag.rssi),
                            border: `1px solid ${getRSSIColor(tag.rssi)}`
                          }}
                        >
                          {tag.rssi} dBm
                        </span>
                      </td>
                      <td data-label="Timestamp" className="timestamp-cell">{formatDate(tag.timestamp)}</td>
                      <td data-label="Actions">
                        <div className="action-buttons">
                          {canEditTag(tag) && (
                            <button
                              className="action-btn edit-btn"
                              onClick={() => {
                                setEditingTag(tag);
                                setTagForm({
                                  tid: tag.tid,
                                  epc: tag.epc,
                                  rssi: tag.rssi,
                                  rid: tag.rid,
                                  state: tag.state || "BLOCKED"
                                });
                                setShowTagModal(true);
                              }}
                              title="Edit Tag"
                            >
                              <i className="fa fa-edit"></i>
                            </button>
                          )}
                          {isManagerOrAdmin() && (
                            <button
                              className="action-btn delete-btn"
                              onClick={() => {
                                setDeleteTarget({ id: tag.tid, name: tag.tid, type: "tag" });
                                setShowDeleteConfirm(true);
                              }}
                              title="Delete Tag"
                            >
                              <i className="fa fa-trash"></i>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {!loading && filteredTags.length > 0 && (
            <div className="pagination">
              <Button
                value="Previous"
                iconLeft="fa-chevron-left"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                variant="outline"
                padding="8px 16px"
              />
              <div className="pagination-info dfcc gap-2">
                <span>Page</span>
                <input
                  type="number"
                  min="1"
                  max={totalFilteredPages}
                  value={pageInput || currentPage}
                  onChange={(e) => setPageInput(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      const newPage = parseInt(pageInput);
                      if (newPage >= 1 && newPage <= totalFilteredPages) {
                        handlePageChange(newPage);
                        setPageInput("");
                      }
                    }
                  }}
                  onBlur={() => {
                    if (pageInput) {
                      const newPage = parseInt(pageInput);
                      if (newPage >= 1 && newPage <= totalFilteredPages) {
                        handlePageChange(newPage);
                      }
                      setPageInput("");
                    }
                  }}
                  style={{
                    width: "60px",
                    height: "36px",
                    borderRadius: "8px",
                    border: "2px solid #E2E8F0",
                    textAlign: "center",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#0F172A"
                  }}
                />
                <span>of {totalFilteredPages}</span>
              </div>
              <Button
                value="Next"
                iconRight="fa-chevron-right"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage >= totalFilteredPages}
                variant="outline"
                padding="8px 16px"
              />
            </div>
          )}
        </div>

      </div>

      {showTagModal && (
        <div className="modal-overlay" onClick={() => setShowTagModal(false)}>
          <div className="modal-container tag-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-wrapper">
              <div className="modal-icon-circle tag-icon-bg">
                <i className="fa fa-tag"></i>
              </div>
              <div className="modal-title-section">
                <span className="modal-title">{editingTag ? "Edit Tag" : "Create New Tag"}</span>
                <span className="modal-subtitle">
                  {editingTag ? "Update tag information and settings" : "Add a new RFID tag to the system"}
                </span>
              </div>
              <div className="modal-close-btn" onClick={() => setShowTagModal(false)}>
                <i className="fa fa-times"></i>
              </div>
            </div>

            <div className="modal-body-wrapper">
              <div className="form-row-group">
                <div className="form-field">
                  <div className="form-label-wrapper">
                    <span className="form-label">Tag ID</span>
                    <span className="form-label-required">*</span>
                  </div>
                  {editingTag ? (
                    <div className="read-only-display">
                      <i className="fa fa-hashtag"></i>
                      <span>{editingTag.tid}</span>
                    </div>
                  ) : (
                    <TextField
                      placeholder="TAG001"
                      value={tagForm.tid}
                      onChange={(e) => setTagForm({ ...tagForm, tid: e.target.value })}
                      variant="outlined"
                    />
                  )}
                </div>

              </div>

              <div className="form-field">
                <div className="form-label-wrapper">
                  <span className="form-label">EPC Code</span>
                  <span className="form-label-required">*</span>
                </div>
                <TextField
                  placeholder="Product EPC (hex, 4-24 chars)"
                  value={tagForm.epc}
                  onChange={(e) => {
                    const v = e.target.value.toUpperCase().replace(/[^0-9A-F]/g, "");
                    setTagForm({ ...tagForm, epc: v });
                  }}
                  maxLength={EPC_MAX_LENGTH}
                  variant="outlined"
                />
              </div>

              <div className="form-row-group">
                <div className="form-field">
                  <div className="form-label-wrapper">
                    <span className="form-label">Antenna</span>
                    <span className="form-label-hint">(derived from room)</span>
                  </div>
                  <TextField
                    type="text"
                    value={tagForm.rid ? `Ant ${ridToAntennaMap[tagForm.rid] || "—"}` : "No room assigned"}
                    disabled
                    variant="outlined"
                  />
                </div>

                <div className="form-field">
                  <div className="form-label-wrapper">
                    <span className="form-label">RSSI Signal</span>
                    <span className="form-label-hint">(dBm)</span>
                  </div>
                  <TextField
                    type="number"
                    placeholder="-45"
                    value={tagForm.rssi}
                    onChange={(e) => setTagForm({ ...tagForm, rssi: parseInt(e.target.value) || -45 })}
                    variant="outlined"
                  />
                </div>
              </div>

              <div className="form-row-group">
                <div className="form-field">
                  <div className="form-label-wrapper">
                  <span className="form-label">Assign to Partition</span>
                  </div>
                  <Dropdown
                    options={roomOptionsForTag}
                    value={String(tagForm.rid)}
                    onChange={(val) => setTagForm({ ...tagForm, rid: parseInt(val) })}
                    variant="outlined"
                  />
                </div>

                <div className="form-field">
                  <div className="form-label-wrapper">
                    <span className="form-label">State</span>
                  </div>
                  <Dropdown
                    options={stateOptions}
                    value={tagForm.state}
                    onChange={(val) => setTagForm({ ...tagForm, state: val })}
                    variant="outlined"
                  />
                </div>
              </div>
            </div>

            <div className="modal-footer-wrapper">
              <div className="modal-action-btn cancel-btn" onClick={() => setShowTagModal(false)}>
                <i className="fa fa-times"></i>
                <span>Cancel</span>
              </div>
              <div
                className="modal-action-btn primary-btn"
                onClick={editingTag ? updateTag : createTag}
              >
                <i className={`fa ${editingTag ? "fa-save" : "fa-plus"}`}></i>
                <span>{editingTag ? "Update Tag" : "Create Tag"}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {showDeleteConfirm && deleteTarget && (
        <div className="modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
          <div className="modal-container delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-wrapper delete-header">
              <div className="modal-icon-circle delete-icon-bg">
                <i className="fa fa-exclamation-triangle"></i>
              </div>
              <div className="modal-title-section">
                <span className="modal-title delete-title">Confirm Delete</span>
                <span className="modal-subtitle delete-subtitle">
                  This action is permanent and cannot be undone
                </span>
              </div>
              <div className="modal-close-btn delete-close" onClick={() => setShowDeleteConfirm(false)}>
                <i className="fa fa-times"></i>
              </div>
            </div>

            <div className="modal-body-wrapper delete-body">
              <div className="delete-message-wrapper">
                <div className="delete-message-icon">
                  <i className="fa fa-info-circle"></i>
                </div>
                <div className="delete-message-content">
                  <span className="delete-message-primary">
                    Are you sure you want to delete <strong>"{deleteTarget.name}"</strong>?
                  </span>
                  <span className="delete-message-secondary">
                    This will permanently remove this tag from the system.
                  </span>
                </div>
              </div>
            </div>

            <div className="modal-footer-wrapper">
              <div
                className="modal-action-btn cancel-btn"
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeleteTarget(null);
                }}
              >
                <i className="fa fa-times"></i>
                <span>Cancel</span>
              </div>
              <div
                className="modal-action-btn delete-btn"
                onClick={confirmDelete}
              >
                <i className="fa fa-trash"></i>
                <span>Delete Tag</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {showDeleteAllConfirm && (
        <div className="modal-overlay" onClick={() => {
          setShowDeleteAllConfirm(false);
          setDeleteAllStep(1);
        }}>
          <div className="modal-container delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-wrapper delete-header">
              <div className="modal-icon-circle delete-icon-bg" style={{
                backgroundColor: deleteAllStep === 2 ? '#b91c1c' : '#dc2626'
              }}>
                <i className={deleteAllStep === 1 ? "fa fa-exclamation-triangle" : "fa fa-exclamation-circle"}></i>
              </div>
              <div className="modal-title-section">
                <span className="modal-title delete-title">
                  {deleteAllStep === 1 ? "Delete All Tags?" : "Final Confirmation"}
                </span>
                <span className="modal-subtitle delete-subtitle">
                  {deleteAllStep === 1
                    ? "Step 1 of 2 - This action is PERMANENT"
                    : "Step 2 of 2 - Last chance to cancel"}
                </span>
              </div>
              <div className="modal-close-btn delete-close" onClick={() => {
                setShowDeleteAllConfirm(false);
                setDeleteAllStep(1);
              }}>
                <i className="fa fa-times"></i>
              </div>
            </div>

            <div className="modal-body-wrapper delete-body">
              <div className="delete-message-wrapper">
                <div className="delete-message-icon" style={{
                  color: deleteAllStep === 2 ? '#b91c1c' : '#dc2626'
                }}>
                  <i className="fa fa-exclamation-triangle"></i>
                </div>
                <div className="delete-message-content">
                  {deleteAllStep === 1 ? (
                    <>
                      <span className="delete-message-primary" style={{ fontSize: '16px', fontWeight: '600' }}>
                        You are about to delete <strong style={{ color: '#dc2626' }}>ALL {totalTags} TAGS</strong> from the system!
                      </span>
                      <span className="delete-message-secondary" style={{ marginTop: '12px', display: 'block' }}>
                        This will permanently remove every tag from all rooms
                      </span>
                    </>
                  ) : (
                    <>
                      <span className="delete-message-primary" style={{
                        fontSize: '18px',
                        fontWeight: '700',
                        color: '#b91c1c',
                        textTransform: 'uppercase'
                      }}>
                        ARE YOU ABSOLUTELY SURE?
                      </span>
                      <span className="delete-message-secondary" style={{
                        marginTop: '16px',
                        display: 'block',
                        fontSize: '15px',
                        fontWeight: '600'
                      }}>
                        This is your last warning before deleting <strong style={{ color: '#b91c1c' }}>ALL {totalTags} TAGS</strong>
                      </span>
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="modal-footer-wrapper">
              <div
                className="modal-action-btn cancel-btn"
                onClick={() => {
                  setShowDeleteAllConfirm(false);
                  setDeleteAllStep(1);
                }}
              >
                <i className="fa fa-times"></i>
                <span>Cancel</span>
              </div>
              {deleteAllStep === 1 ? (
                <div
                  className="modal-action-btn delete-btn"
                  onClick={() => setDeleteAllStep(2)}
                  style={{ backgroundColor: '#dc2626' }}
                >
                  <i className="fa fa-arrow-right"></i>
                  <span>Continue to Step 2</span>
                </div>
              ) : (
                <div
                  className="modal-action-btn delete-btn"
                  onClick={deleteAllTags}
                  style={{
                    backgroundColor: '#b91c1c',
                    fontWeight: '700',
                    fontSize: '14px'
                  }}
                >
                  <i className="fa fa-trash"></i>
                  <span>{loading ? "DELETING..." : "DELETE ALL TAGS"}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showBulkStateModal && (
        <div className="modal-overlay" onClick={() => setShowBulkStateModal(false)}>
          <div className="modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-wrapper">
              <div className="modal-icon-circle tag-icon-bg">
                <i className="fa fa-flag"></i>
              </div>
              <div className="modal-title-section">
                <span className="modal-title">Change State for {selectedTags.length} Tags</span>
                <span className="modal-subtitle">Set the state for all selected tags</span>
              </div>
              <div className="modal-close-btn" onClick={() => setShowBulkStateModal(false)}>
                <i className="fa fa-times"></i>
              </div>
            </div>
            <div className="modal-body-wrapper">
              <div className="form-field">
                <div className="form-label-wrapper">
                  <span className="form-label">New State</span>
                  <span className="form-label-required">*</span>
                </div>
                <Dropdown
                  options={stateOptions}
                  value={bulkEditState}
                  onChange={setBulkEditState}
                  placeholder="Select state"
                />
              </div>
            </div>
            <div className="modal-footer-wrapper">
              <div className="modal-action-btn cancel-btn" onClick={() => setShowBulkStateModal(false)}>
                <i className="fa fa-times"></i>
                <span>Cancel</span>
              </div>
              <div
                className="modal-action-btn primary-btn"
                onClick={bulkUpdateState}
              >
                <i className="fa fa-flag"></i>
                <span>{loading ? "Updating..." : "Update State"}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {showBulkEditModal && (
        <div className="modal-overlay" onClick={() => setShowBulkEditModal(false)}>
          <div className="modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-wrapper">
              <div className="modal-icon-circle tag-icon-bg">
                <i className="fa fa-exchange"></i>
              </div>
              <div className="modal-title-section">
                <span className="modal-title">Change Partition for {selectedTags.length} Tags</span>
                <span className="modal-subtitle">Move selected tags to a different room</span>
              </div>
              <div className="modal-close-btn" onClick={() => setShowBulkEditModal(false)}>
                <i className="fa fa-times"></i>
              </div>
            </div>

            <div className="modal-body-wrapper">
              <div className="form-field">
                <div className="form-label-wrapper">
                  <span className="form-label">Selected Tags</span>
                </div>
                <div style={{
                  maxHeight: '120px',
                  overflowY: 'auto',
                  padding: '12px',
                  backgroundColor: '#F8FAFC',
                  borderRadius: '8px',
                  border: '1px solid #E2E8F0'
                }}>
                  {selectedTags.map((tid, idx) => (
                    <span key={tid} style={{
                      display: 'inline-block',
                      padding: '4px 8px',
                      margin: '2px',
                      backgroundColor: '#E0F2FE',
                      color: '#0369A1',
                      borderRadius: '4px',
                      fontSize: '12px',
                      fontWeight: '500'
                    }}>
                      {tid}
                    </span>
                  ))}
                </div>
              </div>

              <div className="form-field">
                <div className="form-label-wrapper">
                  <span className="form-label">New Partition</span>
                  <span className="form-label-required">*</span>
                </div>
                <Dropdown
                  options={roomOptionsForTag}
                  value={String(bulkEditRid)}
                  onChange={(val) => setBulkEditRid(parseInt(val))}
                  variant="outlined"
                />
              </div>
            </div>

            <div className="modal-footer-wrapper">
              <div className="modal-action-btn cancel-btn" onClick={() => setShowBulkEditModal(false)}>
                <i className="fa fa-times"></i>
                <span>Cancel</span>
              </div>
              <div
                className="modal-action-btn primary-btn"
                onClick={bulkUpdateRoom}
              >
                <i className="fa fa-check"></i>
                <span>{loading ? "Updating..." : "Update Tags"}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {showBulkDeleteConfirm && (
        <div className="modal-overlay" onClick={() => setShowBulkDeleteConfirm(false)}>
          <div className="modal-container delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-wrapper delete-header">
              <div className="modal-icon-circle delete-icon-bg">
                <i className="fa fa-exclamation-triangle"></i>
              </div>
              <div className="modal-title-section">
                <span className="modal-title delete-title">Delete {selectedTags.length} Tags?</span>
                <span className="modal-subtitle delete-subtitle">
                  This action is permanent and cannot be undone
                </span>
              </div>
              <div className="modal-close-btn delete-close" onClick={() => setShowBulkDeleteConfirm(false)}>
                <i className="fa fa-times"></i>
              </div>
            </div>

            <div className="modal-body-wrapper delete-body">
              <div className="delete-message-wrapper">
                <div className="delete-message-icon">
                  <i className="fa fa-info-circle"></i>
                </div>
                <div className="delete-message-content">
                  <span className="delete-message-primary">
                    Are you sure you want to delete <strong>{selectedTags.length} tags</strong>?
                  </span>
                  <span className="delete-message-secondary">
                    The following tags will be permanently removed:
                  </span>
                  <div style={{
                    maxHeight: '100px',
                    overflowY: 'auto',
                    marginTop: '8px',
                    padding: '8px',
                    backgroundColor: '#FEF2F2',
                    borderRadius: '4px'
                  }}>
                    {selectedTags.map(tid => (
                      <span key={tid} style={{
                        display: 'inline-block',
                        padding: '2px 6px',
                        margin: '2px',
                        backgroundColor: '#FECACA',
                        color: '#991B1B',
                        borderRadius: '3px',
                        fontSize: '11px'
                      }}>
                        {tid}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer-wrapper">
              <div
                className="modal-action-btn cancel-btn"
                onClick={() => setShowBulkDeleteConfirm(false)}
              >
                <i className="fa fa-times"></i>
                <span>Cancel</span>
              </div>
              <div
                className="modal-action-btn delete-btn"
                onClick={bulkDeleteTags}
              >
                <i className="fa fa-trash"></i>
                <span>{loading ? "Deleting..." : `Delete ${selectedTags.length} Tags`}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
